import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/fire';
import { FaIdCard, FaCalendarWeek, FaClock, FaUsers, FaEdit, FaTrash, FaPlus, FaTimes, FaCheck } from 'react-icons/fa';

export default function MembershipDetailsTab({ isSecAdmin, theme = 'light' }) {
    const [memberships, setMemberships] = useState([]);
    const [newMembership, setNewMembership] = useState({
        name: '',
        classesPerWeek: '',
        duration: '',
        totalClasses: '',
        isUnlimitedClasses: false,
        isUnlimitedDuration: false
    });
    const [editingMembershipId, setEditingMembershipId] = useState(null);
    const [editedData, setEditedData] = useState({});

    useEffect(() => {
        fetchMemberships();
    }, []);

    const fetchMemberships = async () => {
        const membershipsSnapshot = await getDocs(collection(db, 'membershipDetails'));
        const membershipsList = membershipsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMemberships(membershipsList);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewMembership(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await addDoc(collection(db, 'membershipDetails'), newMembership);
            setNewMembership({
                name: '',
                classesPerWeek: '',
                duration: '',
                totalClasses: '',
                isUnlimitedClasses: false,
                isUnlimitedDuration: false
            });
            fetchMemberships();
        } catch (error) {
            console.error("Error adding membership: ", error);
        }
    };

    const handleStartEdit = (membership) => {
        setEditingMembershipId(membership.id);
        setEditedData({ ...membership });
    };

    const handleCancelEdit = () => {
        setEditingMembershipId(null);
        setEditedData({});
    };

    const handleUpdateMembership = async (id) => {
        try {
            const updateData = { ...editedData };
            delete updateData.id; // Remove id from the update data
            await updateDoc(doc(db, 'membershipDetails', id), updateData);
            setEditingMembershipId(null);
            setEditedData({});
            fetchMemberships();
        } catch (error) {
            console.error("Error updating membership: ", error);
        }
    };

    const handleEditChange = (field, value) => {
        setEditedData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this membership?")) {
            try {
                await deleteDoc(doc(db, 'membershipDetails', id));
                fetchMemberships();
            } catch (error) {
                console.error("Error deleting membership: ", error);
            }
        }
    };

    return (
        <div className={`p-4 sm:p-6 ${
            theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
        }`}>
            <div>
                {/* <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-lg p-6 sm:p-8 mb-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center gap-3">
                        <FaIdCard />
                        Membership Details
                    </h2>
                    <p className="text-blue-100 text-sm sm:text-base">Create and manage membership plans</p>
                </div> */}
                <form onSubmit={handleSubmit} className={`shadow-xl rounded-xl overflow-hidden border mb-8 ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                        <h3 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
                            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                <FaPlus className="text-white" />
                            </div>
                            Add New Membership
                        </h3>
                        <p className="text-blue-50 text-sm mt-1">Fill in the details to create a new membership plan</p>
                    </div>
                    <div className="p-6 sm:p-8">

                    <div className="grid grid-cols-1 gap-6 mb-6">
                        <div>
                            <label className={`block text-sm sm:text-base font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`} htmlFor="name">
                                <FaIdCard className="inline mr-2 text-blue-600" />
                                 Membership Name
                            </label>
                            <input
                                className={`w-full px-4 py-3 border-2 rounded-lg text-sm sm:text-base focus:border-blue-500 focus:outline-none transition-colors shadow-sm ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                }`}
                                id="name"
                                type="text"
                                name="name"
                                value={newMembership.name}
                                onChange={handleInputChange}
                                placeholder="e.g., Gold Membership"
                                disabled={isSecAdmin}
                                required
                            />
                        </div>

                        <div>
                            <label className={`block text-sm sm:text-base font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`} htmlFor="classesPerWeek">
                                <FaCalendarWeek className="inline mr-2 text-blue-600" />
                                Classes Per Week
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    className={`flex-1 px-4 py-3 border-2 rounded-lg text-sm sm:text-base focus:border-blue-500 focus:outline-none transition-colors shadow-sm ${
                                        theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    id="classesPerWeek"
                                    type="number"
                                    name="classesPerWeek"
                                    value={newMembership.classesPerWeek}
                                    onChange={handleInputChange}
                                    placeholder="Number of classes"
                                    disabled={newMembership.isUnlimitedClasses || isSecAdmin}
                                />
                                <label className={`flex items-center gap-2 px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                                    theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                                }`}>
                                    <input
                                        type="checkbox"
                                        name="isUnlimitedClasses"
                                        checked={newMembership.isUnlimitedClasses}
                                        onChange={handleInputChange}
                                        disabled={isSecAdmin}
                                        className="w-4 h-4"
                                    />
                                    <span className={`text-sm sm:text-base font-semibold whitespace-nowrap ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Unlimited</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-6 mb-6">
                        <div>
                            <label className={`block text-sm sm:text-base font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`} htmlFor="totalClasses">
                                <FaUsers className="inline mr-2 text-blue-600" />
                                Total Classes Allowed
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    className={`flex-1 px-4 py-3 border-2 rounded-lg text-sm sm:text-base focus:border-blue-500 focus:outline-none transition-colors shadow-sm ${
                                        theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    id="totalClasses"
                                    type="number"
                                    name="totalClasses"
                                    value={newMembership.totalClasses === 'Unlimited' ? '' : newMembership.totalClasses}
                                    onChange={(e) =>
                                        setNewMembership((prev) => ({
                                            ...prev,
                                            totalClasses: e.target.value,
                                        }))
                                    }
                                    placeholder="Total classes"
                                    disabled={newMembership.totalClasses === 'Unlimited' || isSecAdmin}
                                />
                                <label className={`flex items-center gap-2 px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                                    theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                                }`}>
                                    <input
                                        type="checkbox"
                                        checked={newMembership.totalClasses === 'Unlimited'}
                                        onChange={(e) =>
                                            setNewMembership((prev) => ({
                                                ...prev,
                                                totalClasses: e.target.checked ? 'Unlimited' : ''
                                            }))
                                        }
                                        disabled={isSecAdmin}
                                        className="w-4 h-4"
                                    />
                                    <span className={`text-sm sm:text-base font-semibold whitespace-nowrap ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Unlimited</span>
                                </label>
                            </div>
                        </div>

                        <div>
                            <label className={`block text-sm sm:text-base font-semibold mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`} htmlFor="duration">
                                <FaClock className="inline mr-2 text-blue-600" />
                                Duration (Weeks)
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    className={`flex-1 px-4 py-3 border-2 rounded-lg text-sm sm:text-base focus:border-blue-500 focus:outline-none transition-colors shadow-sm ${
                                        theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    id="duration"
                                    type="number"
                                    name="duration"
                                    value={newMembership.duration}
                                    onChange={handleInputChange}
                                    placeholder="Number of weeks"
                                    disabled={newMembership.isUnlimitedDuration || isSecAdmin}
                                />
                                <label className={`flex items-center gap-2 px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                                    theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                                }`}>
                                    <input
                                        type="checkbox"
                                        name="isUnlimitedDuration"
                                        checked={newMembership.isUnlimitedDuration}
                                        onChange={handleInputChange}
                                        disabled={isSecAdmin}
                                        className="w-4 h-4"
                                    />
                                    <span className={`text-sm sm:text-base font-semibold whitespace-nowrap ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Unlimited</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className={`flex flex-col sm:flex-row gap-3 pt-6 border-t mt-6 ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <button
                            className="flex-1 sm:flex-none bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-3 px-8 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 text-sm sm:text-base flex items-center justify-center gap-2"
                            type="submit"
                            disabled={isSecAdmin}
                        >
                            <FaPlus className="text-lg" />
                            Add Membership
                        </button>
                    </div>
                    </div>
                </form>
                <div className={`shadow-xl rounded-xl overflow-hidden border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                        <h3 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
                            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                <FaIdCard className="text-white" />
                            </div>
                            Existing Memberships
                        </h3>
                        <p className="text-blue-50 text-sm mt-1">View and manage all membership plans</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm sm:text-base">
                            <thead>
                                <tr className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                                    <th className="px-4 py-3 text-left font-bold">Name</th>
                                    <th className="px-4 py-3 text-left font-bold">Classes Per Week</th>
                                    <th className="px-4 py-3 text-left font-bold">Total Classes</th>
                                    <th className="px-4 py-3 text-left font-bold">Duration (Weeks)</th>
                                    <th className="px-4 py-3 text-left font-bold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {memberships.map((membership, index) => {
                                    const isEditing = editingMembershipId === membership.id;
                                    const data = isEditing ? editedData : membership;
                                    
                                    return (
                                        <tr key={membership.id} className={`border-b transition-colors ${
                                            theme === 'dark' 
                                                ? `hover:bg-gray-700 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}` 
                                                : `hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`
                                        }`}>
                                            <td className="px-4 py-3">
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        value={data.name}
                                                        onChange={(e) => handleEditChange('name', e.target.value)}
                                                        className={`w-full px-2 py-1 border rounded focus:border-blue-500 focus:outline-none ${
                                                            theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                        }`}
                                                        disabled={isSecAdmin}
                                                    />
                                                ) : (
                                                    <span className={`font-semibold ${
                                                        theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                                                    }`}>{membership.name}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {isEditing ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            value={data.isUnlimitedClasses ? '' : data.classesPerWeek}
                                                            onChange={(e) => handleEditChange('classesPerWeek', e.target.value)}
                                                            className={`w-20 px-2 py-1 border rounded focus:border-blue-500 focus:outline-none ${
                                                                theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                            }`}
                                                            disabled={data.isUnlimitedClasses || isSecAdmin}
                                                        />
                                                        <label className={`flex items-center gap-1 text-xs whitespace-nowrap ${
                                                            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                                                        }`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={data.isUnlimitedClasses}
                                                                onChange={(e) => handleEditChange('isUnlimitedClasses', e.target.checked)}
                                                                disabled={isSecAdmin}
                                                                className="w-3 h-3"
                                                            />
                                                            Unlimited
                                                        </label>
                                                    </div>
                                                ) : (
                                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm ${
                                                        theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                                                    }`}>
                                                        {membership.isUnlimitedClasses ? 'Unlimited' : membership.classesPerWeek}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {isEditing ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            value={data.totalClasses === 'Unlimited' ? '' : data.totalClasses}
                                                            onChange={(e) => handleEditChange('totalClasses', e.target.value)}
                                                            className={`w-20 px-2 py-1 border rounded focus:border-blue-500 focus:outline-none ${
                                                                theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                            }`}
                                                            disabled={data.totalClasses === 'Unlimited' || isSecAdmin}
                                                        />
                                                        <label className={`flex items-center gap-1 text-xs whitespace-nowrap ${
                                                            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                                                        }`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={data.totalClasses === 'Unlimited'}
                                                                onChange={(e) => handleEditChange('totalClasses', e.target.checked ? 'Unlimited' : '')}
                                                                disabled={isSecAdmin}
                                                                className="w-3 h-3"
                                                            />
                                                            Unlimited
                                                        </label>
                                                    </div>
                                                ) : (
                                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm ${
                                                        theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                                                    }`}>
                                                        {membership.totalClasses}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {isEditing ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            value={data.isUnlimitedDuration ? '' : data.duration}
                                                            onChange={(e) => handleEditChange('duration', e.target.value)}
                                                            className={`w-20 px-2 py-1 border rounded focus:border-blue-500 focus:outline-none ${
                                                                theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                            }`}
                                                            disabled={data.isUnlimitedDuration || isSecAdmin}
                                                        />
                                                        <label className={`flex items-center gap-1 text-xs whitespace-nowrap ${
                                                            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                                                        }`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={data.isUnlimitedDuration}
                                                                onChange={(e) => handleEditChange('isUnlimitedDuration', e.target.checked)}
                                                                disabled={isSecAdmin}
                                                                className="w-3 h-3"
                                                            />
                                                            Unlimited
                                                        </label>
                                                    </div>
                                                ) : (
                                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm ${
                                                        theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                                                    }`}>
                                                        {membership.isUnlimitedDuration ? 'Unlimited' : membership.duration}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-2">
                                                    {isEditing ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleUpdateMembership(membership.id)}
                                                                disabled={isSecAdmin}
                                                                className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-3 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center gap-1 text-xs sm:text-sm"
                                                            >
                                                                <FaCheck />
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEdit}
                                                                disabled={isSecAdmin}
                                                                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-3 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center gap-1 text-xs sm:text-sm"
                                                            >
                                                                <FaTimes />
                                                                Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => handleStartEdit(membership)}
                                                                disabled={isSecAdmin}
                                                                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-3 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center gap-1 text-xs sm:text-sm"
                                                            >
                                                                <FaEdit />
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(membership.id)}
                                                                disabled={isSecAdmin}
                                                                className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-3 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center gap-1 text-xs sm:text-sm"
                                                            >
                                                                <FaTrash />
                                                                Delete
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}